import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pause, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import MusicStaff from '../components/MusicStaff';
import VirtualPiano from '../components/VirtualPiano';
import SessionTimer from '../components/SessionTimer';
import SessionCompleteOverlay from '../components/SessionCompleteOverlay';
import { useApp } from '../context/AppContext';
import { useAudio } from '../hooks/useAudio';
import { selectNextNote, calculateSessionMetrics } from '../utils/spacedRepetition';

const SESSION_CONFIG = {
  timed: { duration: 60 },
  count: { totalCards: 20, warmupCards: 4 }
};

const Practice = memo(function Practice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'timed';
  
  const { notes, currentRange, settings, updateNoteStats, unlockNewNote } = useApp();
  const { initAudio, playNote, playCorrectNote, playIncorrect, isLoaded: audioLoaded } = useAudio(settings.soundEnabled);
  
  // Session state
  const [currentNote, setCurrentNote] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(SESSION_CONFIG.timed.duration);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);
  
  // Session complete overlay state
  const [showSessionComplete, setShowSessionComplete] = useState(false);
  const [sessionMetrics, setSessionMetrics] = useState(null);
  
  // Track mastered notes during session (no interruption)
  const [masteredNotesDuringSession, setMasteredNotesDuringSession] = useState([]);
  
  // Feedback state
  const [feedbackKey, setFeedbackKey] = useState(null);
  const [feedbackType, setFeedbackType] = useState(null);
  const [showNoteName, setShowNoteName] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  
  // Timing refs
  const noteStartTime = useRef(Date.now());
  const timerRef = useRef(null);
  const recentNotesRef = useRef([]);
  
  // Initialize audio on first interaction
  useEffect(() => {
    if (settings.soundEnabled && !audioLoaded) {
      initAudio();
    }
  }, [settings.soundEnabled, audioLoaded, initAudio]);
  
  // Generate next note (excludes the specified note to avoid repetition)
  const getNextNote = useCallback((excludeNote = null) => {
    const recent = recentNotesRef.current;
    const nextNote = selectNextNote(notes, currentRange.unlockedNotes, recent, excludeNote);

    // Update recent notes buffer
    recentNotesRef.current = [...recent.slice(-2), nextNote];
    
    return nextNote;
  }, [notes, currentRange.unlockedNotes]);
  
  // Initialize first note
  useEffect(() => {
    if (!currentNote && !isComplete) {
      setCurrentNote(getNextNote());
      noteStartTime.current = Date.now();
    }
  }, [currentNote, isComplete, getNextNote]);
  
  // Timer logic for timed mode
  useEffect(() => {
    if (mode !== 'timed' || isPaused || isComplete) return;
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsComplete(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerRef.current);
  }, [mode, isPaused, isComplete]);
  
  // Check for count mode completion
  useEffect(() => {
    if (mode === 'count' && currentCardIndex >= SESSION_CONFIG.count.totalCards) {
      setIsComplete(true);
    }
  }, [mode, currentCardIndex]);
  
  // Show session complete overlay when session ends
  useEffect(() => {
    if (isComplete && !showSessionComplete) {
      const metrics = calculateSessionMetrics(sessionResults);
      setSessionMetrics(metrics);
      setShowSessionComplete(true);
    }
  }, [isComplete, showSessionComplete, sessionResults]);
  
  // Handle session complete overlay continue
  const handleSessionCompleteContinue = useCallback(() => {
    // Store results in sessionStorage for Summary page
    sessionStorage.setItem('sessionResults', JSON.stringify({
      mode,
      results: sessionResults,
      metrics: sessionMetrics
    }));
    navigate('/summary');
  }, [mode, sessionResults, sessionMetrics, navigate]);
  
  // Handle key press
  const handleKeyPress = useCallback(async (pressedNote) => {
    if (isPaused || isComplete || feedbackKey) return;
    
    // Initialize audio on first user interaction (required for iOS)
    if (settings.soundEnabled && !audioLoaded) {
      await initAudio();
    }
    
    const responseTime = Date.now() - noteStartTime.current;
    const isCorrect = pressedNote === currentNote;
    
    if (isCorrect) {
      // Correct answer
      setFeedbackKey(pressedNote);
      setFeedbackType('correct');
      
      // Play the actual correct note sound (realistic piano)
      if (settings.soundEnabled) {
        playCorrectNote(currentNote);
      }
      
      // Record result (only count first attempt as correct)
      const result = {
        note: currentNote,
        isCorrect: attemptCount === 0,
        responseTime,
        attempts: attemptCount + 1
      };
      setSessionResults(prev => [...prev, result]);
      
      // Update stats and check for mastery
      const { newlyMastered } = updateNoteStats(currentNote, attemptCount === 0, responseTime);
      
      // If note was just mastered, track it (no interruption)
      if (newlyMastered) {
        setMasteredNotesDuringSession(prev => [...prev, currentNote]);
        // Unlock a new note silently
        unlockNewNote();
      }
      
      // Move to next note after delay
      setTimeout(() => {
        setFeedbackKey(null);
        setFeedbackType(null);
        setShowNoteName(false);
        setAttemptCount(0);
        setCurrentNote(getNextNote(currentNote));  // Exclude current note
        setCurrentCardIndex(prev => prev + 1);
        noteStartTime.current = Date.now();
      }, 600);
      
    } else {
      // Incorrect answer
      setFeedbackKey(pressedNote);
      setFeedbackType('incorrect');
      
      // Play soft descending chime for incorrect
      if (settings.soundEnabled) {
        playIncorrect();
      }
      
      if (attemptCount === 0) {
        // First wrong attempt - show feedback and allow second try
        setAttemptCount(1);
        setTimeout(() => {
          setFeedbackKey(null);
          setFeedbackType(null);
        }, 400);
      } else {
        // Second wrong attempt - show correct answer and move on
        setShowNoteName(true);
        
        // Record as incorrect
        const result = {
          note: currentNote,
          isCorrect: false,
          responseTime,
          attempts: attemptCount + 1
        };
        setSessionResults(prev => [...prev, result]);
        updateNoteStats(currentNote, false, responseTime);
        
        // Move to next after showing answer
        setTimeout(() => {
          setFeedbackKey(null);
          setFeedbackType(null);
          setShowNoteName(false);
          setAttemptCount(0);
          setCurrentNote(getNextNote(currentNote));  // Exclude current note
          setCurrentCardIndex(prev => prev + 1);
          noteStartTime.current = Date.now();
        }, 1500);
      }
    }
  }, [isPaused, isComplete, feedbackKey, currentNote, attemptCount, settings.soundEnabled, audioLoaded, initAudio, playCorrectNote, playIncorrect, updateNoteStats, unlockNewNote, getNextNote]);
  
  // Handle pause/resume
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);
  
  // Handle exit
  const handleExit = useCallback(() => {
    if (sessionResults.length > 0) {
      const confirmed = window.confirm('Exit session? Your progress will be saved.');
      if (!confirmed) return;
      
      // Save partial session
      const metrics = calculateSessionMetrics(sessionResults);
      sessionStorage.setItem('sessionResults', JSON.stringify({
        mode,
        results: sessionResults,
        metrics,
        partial: true
      }));
      navigate('/summary');
    } else {
      navigate('/');
    }
  }, [navigate, mode, sessionResults]);

  return (
    <div className="h-[100dvh] bg-cream-100 flex flex-col" data-testid="practice-screen">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 sm:py-3 border-b border-wood-300 bg-cream-100/80 backdrop-blur-sm flex-shrink-0">
        <button
          onClick={handleExit}
          className="icon-btn w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{ backgroundColor: 'rgba(107, 127, 140, 0.1)' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
          onMouseDown={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.3)'}
          onMouseUp={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
          aria-label="Exit session"
          data-testid="exit-session-btn"
        >
          <X className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
        </button>
        
        <SessionTimer
          mode={mode}
          timeRemaining={timeRemaining}
          currentCard={currentCardIndex}
          totalCards={SESSION_CONFIG.count.totalCards}
          isPaused={isPaused}
        />
        
        <button
          onClick={togglePause}
          className="icon-btn w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{ backgroundColor: 'rgba(107, 127, 140, 0.1)' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
          onMouseDown={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.3)'}
          onMouseUp={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
          aria-label={isPaused ? 'Resume' : 'Pause'}
          data-testid="pause-btn"
        >
          {isPaused ? (
            <Play className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          ) : (
            <Pause className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          )}
        </button>
      </header>
      
      {/* Main Practice Area - responsive flex, smaller on mobile to leave room for piano */}
      <main className="flex-1 min-h-0 flex flex-col items-center justify-center px-4 py-2 relative overflow-visible">
        {/* Pause Overlay */}
        <AnimatePresence>
          {isPaused && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-cream-100/90 backdrop-blur-sm z-10 flex items-center justify-center"
            >
              <div className="text-center">
                <h2 className="font-serif text-2xl text-cream-900 mb-4">Paused</h2>
                <Button
                  onClick={togglePause}
                  className="bg-coral-500 hover:bg-coral-600 active:bg-coral-700 text-white font-sans font-medium rounded-btn transition-colors"
                  data-testid="resume-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Staff Display - Responsive sizing */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentNote}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl flex flex-col items-center justify-center"
          >
            {currentNote && (
              <MusicStaff
                note={currentNote}
                showNoteName={showNoteName}
                learningMode={settings.learningMode}
              />
            )}
          </motion.div>
        </AnimatePresence>
        
        {/* Attempt indicator */}
        {attemptCount > 0 && !showNoteName && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-coral-500 font-sans mt-4"
          >
            Try again! One more chance.
          </motion.p>
        )}
      </main>
      
      {/* Piano Keyboard Section - fixed height on mobile, flex on desktop */}
      <div className="h-[180px] sm:h-[200px] md:h-[220px] flex-shrink-0 flex flex-col">
        <VirtualPiano
          onKeyPress={handleKeyPress}
          feedbackKey={feedbackKey}
          feedbackType={feedbackType}
          showNoteNames={settings.learningMode || settings.showNoteNames}
          availableNotes={currentRange.unlockedNotes}
          disabled={isPaused || !!feedbackKey || showSessionComplete}
        />
      </div>
      
      {/* Session Complete Overlay */}
      <SessionCompleteOverlay
        isVisible={showSessionComplete}
        metrics={sessionMetrics}
        mode={mode}
        masteredNotes={masteredNotesDuringSession}
        onContinue={handleSessionCompleteContinue}
      />
    </div>
  );
});

export default Practice;
